import { imputePayment, type ImputableInstallment } from '../imputation/index.js';
import { moraForInstallment } from '../mora/index.js';
import {
  amortizeRemainingReduceCuota,
  amortizeRemainingFixedPayment,
} from '../amortization/remaining.js';
import type { AmortizationRow } from '../amortization/types.js';
import type { ImputationSnapshot } from '../schemas/payment.js';
import type { ImputationOrder, DayCount, RoundingMode } from '../schemas/project.js';

export type PaydownMode = 'reduce_cuota' | 'reduce_plazo';

/** The installment fields the abono math needs (a subset of the DB row). */
export type AbonoInstallment = {
  id: string;
  installmentNumber: number;
  dueDate: string;
  principalAmount: string;
  interestAmount: string;
  totalAmount: string;
  paidPrincipal: string;
  paidInterest: string;
  adminFeeAmount: string;
  paidAdminFee: string;
  paidMora: string;
};

export type AbonoParams = {
  installments: readonly AbonoInstallment[];
  amount: string;
  paymentDate: string;
  order: ImputationOrder;
  annualRate: string;
  startDate: string;
  dayCount: DayCount;
  roundingMode: RoundingMode;
  moraMonthlyRate: string;
  moraGraceDays: number;
  /** 1-based month interest starts (see contracts). Defaults to 1 (no grace). */
  interestStartMonth?: number;
  mode: PaydownMode;
};

export type AbonoResult = {
  /** Waterfall allocation over the currently-due installments (the `D` split). */
  dueSnapshot: ImputationSnapshot;
  /** Total applied to current dues (D). */
  appliedToDues: string;
  /** Extra applied to principal beyond current dues (E), capped at the remaining
   *  principal. '0.00' if none. */
  principalPaydown: string;
  /** Genuine excedente: extra-to-principal beyond what remained to be paid down.
   *  '0.00' when the abono doesn't overshoot the remaining principal. */
  unapplied: string;
  /** Outstanding principal after the paydown (B1). */
  balanceAfter: string;
  /** Recomputed future installments (empty when there is no paydown). */
  futureRows: AmortizationRow[];
  oldRemainingCount: number;
  newRemainingCount: number;
  /** Current level cuota (first future installment total) and the new one. */
  oldCuota: string;
  newCuota: string;
};

const num = (s: string): number => Number(s);

export function computeAbono(p: AbonoParams): AbonoResult {
  const asc = [...p.installments].sort((a, b) => a.installmentNumber - b.installmentNumber);
  const due = asc.filter((i) => i.dueDate <= p.paymentDate);
  const future = asc.filter((i) => i.dueDate > p.paymentDate);

  // Remaining grace = future installments still before the interest-start month.
  const interestStartMonth = p.interestStartMonth ?? 1;
  const graceRemaining = future.filter((i) => i.installmentNumber < interestStartMonth).length;

  // 1. Waterfall over the currently-due installments only. Its `unapplied` is E.
  const imputable: ImputableInstallment[] = due.map((r) => {
    const paid = num(r.paidInterest) + num(r.paidPrincipal);
    const unpaidCuota = Math.max(0, num(r.totalAmount) - paid).toFixed(2);
    const { moraAmount } = moraForInstallment({
      unpaidCuota,
      dueDate: r.dueDate,
      asOf: p.paymentDate,
      monthlyRate: p.moraMonthlyRate,
      graceDays: p.moraGraceDays,
    });
    const moraOutstanding = Math.max(0, num(moraAmount) - num(r.paidMora)).toFixed(2);
    return {
      id: r.id,
      installmentNumber: r.installmentNumber,
      interestAmount: r.interestAmount,
      principalAmount: r.principalAmount,
      paidInterest: r.paidInterest,
      paidPrincipal: r.paidPrincipal,
      adminFeeAmount: r.adminFeeAmount,
      paidAdminFee: r.paidAdminFee,
      moraOutstanding,
    };
  });
  const { snapshot: dueSnapshot, appliedTotal: appliedToDues } = imputePayment(
    imputable,
    p.amount,
    p.order,
  );
  const E = num(dueSnapshot.unapplied);

  const principalPaidByDue = dueSnapshot.lines.reduce((s, l) => s + num(l.appliedToPrincipal), 0);
  const outstandingBefore = asc.reduce(
    (s, r) => s + (num(r.principalAmount) - num(r.paidPrincipal)),
    0,
  );
  const oldRemainingCount = future.length;
  const oldCuota = future[0]?.totalAmount ?? '0.00';

  // No extra-to-principal: caller treats this as a regular payment (no recompute).
  if (E <= 0.005) {
    return {
      dueSnapshot,
      appliedToDues,
      principalPaydown: '0.00',
      unapplied: '0.00',
      balanceAfter: Math.max(0, outstandingBefore - principalPaidByDue).toFixed(2),
      futureRows: [],
      oldRemainingCount,
      newRemainingCount: oldRemainingCount,
      oldCuota,
      newCuota: oldCuota,
    };
  }

  const remainingPrincipal = outstandingBefore - principalPaidByDue;
  const effectivePaydown = Math.min(E, remainingPrincipal);
  const overpayment = E - effectivePaydown;
  const B1 = remainingPrincipal - effectivePaydown; // = max(0, remainingPrincipal - E); balanceAfter unchanged
  const balanceAfter = Math.max(0, B1).toFixed(2);
  const prevDate = due.length ? due[due.length - 1]!.dueDate : p.startDate;
  const dueDates = future.map((i) => i.dueDate);

  let futureRows: AmortizationRow[] = [];
  if (B1 > 0.005 && dueDates.length > 0) {
    futureRows =
      p.mode === 'reduce_cuota'
        ? amortizeRemainingReduceCuota({
            balance: balanceAfter,
            annualRate: p.annualRate,
            dueDates,
            prevDate,
            dayCount: p.dayCount,
            graceRemaining,
            roundingMode: p.roundingMode,
          }).rows
        : amortizeRemainingFixedPayment({
            balance: balanceAfter,
            annualRate: p.annualRate,
            dueDates,
            prevDate,
            dayCount: p.dayCount,
            graceRemaining,
            roundingMode: p.roundingMode,
            fixedPayment: oldCuota,
          }).rows;
  }

  const newCuota = p.mode === 'reduce_cuota' ? futureRows[0]?.totalAmount ?? '0.00' : oldCuota;

  return {
    dueSnapshot,
    appliedToDues,
    principalPaydown: effectivePaydown.toFixed(2),
    unapplied: overpayment.toFixed(2),
    balanceAfter,
    futureRows,
    oldRemainingCount,
    newRemainingCount: futureRows.length,
    oldCuota,
    newCuota,
  };
}
